/**
 * Product Identification Modal Component
 * =======================================
 * Modal for displaying AI product identification results.
 * Shows preview of identified data and images before applying to form.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { X, Loader2, Check, AlertCircle, Sparkles, Camera } from 'lucide-react'
import { productIdentifyApi, ProductIdentificationResponse, ProductIdentificationImage } from '@/services/api'

interface ProductIdentificationModalProps {
  imageBase64: string
  onApply: (data: {
    name?: string
    description?: string
    manufacturer?: string
    category?: string
    compatible_products?: string
    imageUrl?: string
  }) => void
  onClose: () => void
}

export default function ProductIdentificationModal({ imageBase64, onApply, onClose }: ProductIdentificationModalProps) {
  const [result, setResult] = useState<ProductIdentificationResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Track which fields user wants to apply
  const [applyFields, setApplyFields] = useState({
    name: true,
    description: true,
    manufacturer: true,
    category: false, // Category needs special handling
    compatible_products: true,
    image: true,
  })

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Perform product identification when modal opens
  useEffect(() => {
    const identifyProduct = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await productIdentifyApi.identify(imageBase64)
        setResult(response)
        if (response.error) {
          setError(response.error)
        } else if (!response.product) {
          setError('Could not identify the product from the image.')
        } else {
          // Auto-select captured image (it will be first in the list)
          setSelectedImage(imageBase64)
        }
      } catch (err) {
        console.error('Product identification failed:', err)
        setError('Failed to identify product. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    identifyProduct()
  }, [imageBase64])

  // Handle apply
  const handleApply = useCallback(() => {
    if (!result?.product) return

    const data: Parameters<typeof onApply>[0] = {}

    if (applyFields.name && result.product.name) {
      data.name = result.product.name
    }
    if (applyFields.description && result.product.description) {
      data.description = result.product.description
    }
    if (applyFields.manufacturer && result.product.manufacturer) {
      data.manufacturer = result.product.manufacturer
    }
    if (applyFields.category && result.product.category) {
      data.category = result.product.category
    }
    if (applyFields.compatible_products && result.product.compatible_products) {
      data.compatible_products = result.product.compatible_products
    }
    if (applyFields.image && selectedImage) {
      data.imageUrl = selectedImage
    }

    onApply(data)
    onClose()
  }, [result, applyFields, selectedImage, onApply, onClose])

  // Combine captured photo with web search results
  // Captured photo is first and marked as "Your Photo"
  const allImages = useMemo((): (ProductIdentificationImage & { isCaptured?: boolean })[] => {
    const capturedImage: ProductIdentificationImage & { isCaptured: boolean } = {
      url: imageBase64,
      thumbnail: imageBase64,
      title: 'Your Photo',
      width: null,
      height: null,
      isCaptured: true,
    }
    return [capturedImage, ...(result?.images || [])]
  }, [imageBase64, result?.images])

  // Confidence badge color
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
              <Sparkles size={20} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Product Identified
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                AI has analyzed your product photo
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={40} className="animate-spin text-primary-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-300 font-medium">Identifying product...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Analyzing image, searching the web, and extracting details with AI
              </p>
            </div>
          ) : error && !result?.product ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={40} className="text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Try taking another photo or enter details manually
              </p>
            </div>
          ) : result?.product ? (
            <div className="space-y-6">
              {/* Show captured image thumbnail */}
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden border dark:border-gray-700 flex-shrink-0">
                  <img
                    src={imageBase64}
                    alt="Captured product"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  {/* Confidence indicator */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${getConfidenceColor(result.product.confidence)}`}>
                      {result.product.confidence.toUpperCase()} CONFIDENCE
                    </span>
                    {result.product.category && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Category: {result.product.category}
                      </span>
                    )}
                  </div>
                  {result.product.search_query && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Search query: <span className="font-mono">{result.product.search_query}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Extracted fields */}
              <div className="space-y-4">
                {/* Name */}
                {result.product.name && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Product Name
                        </label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {result.product.name}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.name}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, name: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Description */}
                {result.product.description && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Description
                        </label>
                        <p className="mt-1 text-gray-700 dark:text-gray-300 text-sm">
                          {result.product.description}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.description}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, description: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Manufacturer */}
                {result.product.manufacturer && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Manufacturer
                        </label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {result.product.manufacturer}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.manufacturer}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, manufacturer: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Compatible Products */}
                {result.product.compatible_products && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Compatible Products
                        </label>
                        <p className="mt-1 text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                          {result.product.compatible_products}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.compatible_products}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, compatible_products: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Images - always show since we have at least the captured photo */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Product Images ({allImages.length})
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyFields.image}
                        onChange={(e) => setApplyFields(prev => ({ ...prev, image: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Apply selected</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {allImages.map((image, index) => {
                      const isCaptured = 'isCaptured' in image && image.isCaptured
                      return (
                        <div
                          key={`${isCaptured ? 'captured' : image.url}-${index}`}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === image.url
                              ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
                              : isCaptured
                                ? 'border-purple-300 dark:border-purple-600 hover:border-purple-400 dark:hover:border-purple-500'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                          onClick={() => setSelectedImage(image.url)}
                          title={isCaptured ? 'Your captured photo' : image.width && image.height ? `${image.width}×${image.height}px` : image.title}
                        >
                          <div className="aspect-square bg-gray-100 dark:bg-gray-700">
                            <img
                              src={image.thumbnail}
                              alt={image.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                if (target.src !== image.url) {
                                  target.src = image.url
                                }
                              }}
                            />
                          </div>
                          {/* Label for captured photo or size for web images */}
                          {isCaptured ? (
                            <div className="absolute bottom-0 left-0 right-0 text-center py-0.5 text-[10px] font-medium bg-purple-500/90 text-white flex items-center justify-center gap-1">
                              <Camera size={10} />
                              Your Photo
                            </div>
                          ) : image.width && image.height && (
                            <div className={`absolute bottom-0 left-0 right-0 text-center py-0.5 text-[10px] font-medium ${
                              image.width >= 800 || image.height >= 800
                                ? 'bg-green-500/90 text-white'
                                : image.width >= 400 || image.height >= 400
                                  ? 'bg-yellow-500/90 text-white'
                                  : 'bg-gray-500/90 text-white'
                            }`}>
                              {image.width}×{image.height}
                            </div>
                          )}
                          {selectedImage === image.url && (
                            <div className="absolute top-1 right-1 bg-primary-500 text-white rounded-full p-0.5">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {result?.product && (
              <>
                {[
                  applyFields.name && result.product.name && 'name',
                  applyFields.description && result.product.description && 'description',
                  applyFields.manufacturer && result.product.manufacturer && 'manufacturer',
                  applyFields.compatible_products && result.product.compatible_products && 'compatible_products',
                  applyFields.image && selectedImage && 'image',
                ].filter(Boolean).length} fields selected
              </>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!result?.product}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                result?.product
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check size={16} />
              Apply to Form
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
